import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Download, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "../services/api";
import { track } from "../utils/track";
import { rowsToXlsxBlob } from "../utils/xlsx";
import {
  buildMonthSheets,
  buildQboCsv,
  entriesInPeriod,
  loadedInfo,
  periodStartIso,
  recentMonths,
  QBO_MAX_BILLS,
} from "../utils/invoiceExport";

// «Descargar el mes» (3-11-2026): todas las facturas de un periodo, no solo la página del
// historial que se ve. Recorre `GET /scanners/{id}/history` (ordenado por `gsi1sk`, más
// recientes primero, 50 por página) y filtra por la fecha de la factura (o la de subida si no
// se lee). Excel con una hoja de facturas (y otra de líneas si alguna las trae) o CSV para
// QuickBooks Online.

const PAGE = 50;
const MAX_PAGES = 60; // 3.000 documentos: de sobra para un mes de una pyme
const MARGIN_DAYS = 2; // `gsi1sk` es la hora de fin de proceso, no la de subida: margen al parar
const STORE_KEY = "qbo_export_defaults";

function readDefaults() {
  try {
    return JSON.parse(window.localStorage.getItem(STORE_KEY) || "{}") || {};
  } catch (_) {
    return {};
  }
}

function saveFile(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const minusDays = (iso, days) => {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - days);
  return d.toISOString().slice(0, 10);
};

/**
 * Todas las entradas desde `startIso` (con margen de 2 días), recorriendo el historial sin
 * repetir resultados. Para si el cursor se repite o al llegar al tope de páginas.
 */
export async function loadHistorySince(scannerId, startIso, getPage = api.getScannerHistory, onProgress) {
  const stopBefore = minusDays(startIso, MARGIN_DAYS);
  const all = [];
  const ids = new Set();
  const cursors = new Set();
  let cursor = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const res = await getPage(scannerId, { limit: PAGE, ...(cursor ? { cursor } : {}) });
    const items = res?.items || [];
    items.forEach((it) => {
      const k = it?.resultId;
      if (k && ids.has(k)) return;
      if (k) ids.add(k);
      all.push(it);
    });
    if (onProgress) onProgress(all.length);
    const next = res?.nextCursor || null;
    const oldest = items.length ? String(items[items.length - 1].createdAt || "").slice(0, 10) : "";
    if (!next || !items.length || (oldest && oldest < stopBefore)) return { items: all, complete: true };
    if (cursors.has(next)) return { items: all, complete: false };
    cursors.add(next);
    cursor = next;
  }
  return { items: all, complete: false };
}

export default function MonthExportModal({ scannerId, scannerName, scannerFields, roles, onClose }) {
  const { t, i18n } = useTranslation();
  const months = useMemo(() => recentMonths(new Date(), 13), []);
  // Por defecto, el mes anterior (el que se cierra con la gestoría).
  const [fromYm, setFromYm] = useState(months[1]);
  const [toYm, setToYm] = useState(months[1]);
  const [format, setFormat] = useState("excel");
  const defaults = useMemo(readDefaults, []);
  const [account, setAccount] = useState(defaults.account || "");
  const [taxCode, setTaxCode] = useState(defaults.taxCode || "");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(0);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [pending, setPending] = useState(null); // CSV de QuickBooks con más de 100 facturas, a confirmar

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-").map(Number);
    try {
      return new Date(y, m - 1, 1).toLocaleDateString(i18n.language, { month: "long", year: "numeric" });
    } catch (_) {
      return ym;
    }
  };

  const [a, b] = fromYm <= toYm ? [fromYm, toYm] : [toYm, fromYm];
  const stamp = a === b ? a : `${a}_${b}`;

  const downloadQbo = (res) => {
    try {
      window.localStorage.setItem(STORE_KEY, JSON.stringify({ account: account.trim(), taxCode: taxCode.trim() }));
    } catch (_) {
      /* sin almacenamiento: no pasa nada */
    }
    saveFile(new Blob([res.csv], { type: "text/csv;charset=utf-8" }), `quickbooks-bills-${stamp}.csv`);
    track("export", { format: "qbo_csv", where: "month_modal", n: res.bills });
    setPending(null);
    setDone({ qbo: true, ...res });
  };

  const run = async () => {
    setError(null);
    setDone(null);
    setPending(null);
    if (format === "qbo" && !account.trim()) {
      setError(t("monthExport.need_account"));
      return;
    }
    setBusy(true);
    setLoaded(0);
    try {
      const { items, complete } = await loadHistorySince(scannerId, periodStartIso(a), api.getScannerHistory, setLoaded);
      const inPeriod = entriesInPeriod(items, roles, a, b);
      if (!inPeriod.length) {
        setError(t("monthExport.none"));
        return;
      }
      if (format === "excel") {
        const keys = ["supplier", "taxId", "number", "date", "dueDate", "net", "tax", "total", "currency", "file", "channel", "uploaded", "dateBasis", "basisInvoice", "basisUpload", "duplicate", "yes"];
        const labels = Object.fromEntries(keys.map((k) => [k, t(`monthExport.col_${k}`)]));
        const { headers, lines, lineCount } = buildMonthSheets(inPeriod, roles, labels, {
          channelLabel: (k) => t(`monthExport.channel_${k}`),
          scannerFields,
        });
        const sheets = [{ name: t("monthExport.sheet_invoices"), rows: headers }];
        if (lineCount) sheets.push({ name: t("monthExport.sheet_lines"), rows: lines });
        saveFile(await rowsToXlsxBlob(sheets), `invoices-${stamp}.xlsx`);
        track("export", { format: "month_excel", where: "month_modal", n: inPeriod.length });
        setDone({ n: inPeriod.length, lines: lineCount, complete });
      } else {
        const res = { ...buildQboCsv(inPeriod, roles, { account, taxCode, loaded: loadedInfo(items) }), complete };
        if (!res.bills) {
          setDone({ qbo: true, ...res });
          return;
        }
        // Aviso ANTES de descargar si pasa de lo que QuickBooks recomienda por importación.
        if (res.bills > QBO_MAX_BILLS) setPending(res);
        else downloadQbo(res);
      }
    } catch (err) {
      setError(t("monthExport.error"));
    } finally {
      setBusy(false);
    }
  };

  const skippedText = (d) =>
    ["duplicate", "credit_note", "missing"]
      .filter((k) => d.skippedBy && d.skippedBy[k])
      .map((k) => t(`monthExport.skipped_${k}`, { count: d.skippedBy[k] }))
      .join(" · ");

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 p-2 sm:p-4 overflow-y-auto">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl my-4">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 p-4 sm:p-5">
          <div>
            <h3 className="text-lg font-bold text-gray-900">{t("monthExport.title")}</h3>
            <p className="mt-1 text-sm text-gray-500">{t("monthExport.subtitle", { name: scannerName || "" })}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-gray-400 hover:bg-gray-100" aria-label={t("monthExport.close")}>
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 p-4 sm:p-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-semibold text-gray-600">
              {t("monthExport.from")}
              <select className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm" value={fromYm} onChange={(e) => setFromYm(e.target.value)}>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-semibold text-gray-600">
              {t("monthExport.to")}
              <select className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm" value={toYm} onChange={(e) => setToYm(e.target.value)}>
                {months.map((m) => (
                  <option key={m} value={m}>
                    {monthLabel(m)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="text-xs text-gray-500">{t("monthExport.date_basis")}</p>

          <fieldset className="space-y-2">
            <legend className="text-xs font-semibold text-gray-600">{t("monthExport.format")}</legend>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="mfmt" checked={format === "excel"} onChange={() => setFormat("excel")} className="mt-1" />
              <span>
                <span className="font-semibold">{t("monthExport.excel")}</span>
                <span className="block text-xs text-gray-500">{t("monthExport.excel_hint")}</span>
              </span>
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="radio" name="mfmt" checked={format === "qbo"} onChange={() => setFormat("qbo")} className="mt-1" />
              <span>
                <span className="font-semibold">{t("monthExport.qbo")}</span>
                <span className="block text-xs text-gray-500">{t("monthExport.qbo_hint", { max: QBO_MAX_BILLS })}</span>
              </span>
            </label>
          </fieldset>

          {format === "qbo" && (
            <div className="space-y-2">
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="text-xs font-semibold text-gray-600">
                  {t("monthExport.qbo_account")}
                  <input className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm" value={account} onChange={(e) => setAccount(e.target.value)} placeholder={t("monthExport.qbo_account_ph")} />
                </label>
                <label className="text-xs font-semibold text-gray-600">
                  {t("monthExport.qbo_tax_code")}
                  <input className="mt-1 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-sm" value={taxCode} onChange={(e) => setTaxCode(e.target.value)} placeholder={t("monthExport.qbo_tax_code_ph")} />
                </label>
              </div>
              {!taxCode.trim() && <p className="text-xs text-amber-700">{t("monthExport.qbo_tax_code_empty")}</p>}
            </div>
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              {error}
            </p>
          )}
          {pending && (
            <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
              <p className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                {t("monthExport.qbo_too_many", { count: pending.bills, max: QBO_MAX_BILLS })}
              </p>
              <button type="button" onClick={() => downloadQbo(pending)} className="mt-2 rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-700">
                {t("monthExport.download_anyway")}
              </button>
            </div>
          )}
          {done && (
            <p className="flex items-start gap-2 rounded-lg bg-green-50 p-3 text-sm text-green-800">
              <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                {done.qbo ? t("monthExport.done_qbo", { count: done.bills }) : t("monthExport.done_excel", { count: done.n })}
                {!done.qbo && done.lines > 0 && <span> {t("monthExport.done_lines", { count: done.lines })}</span>}
                {done.qbo && done.skipped > 0 && <span className="block">{t("monthExport.skipped_title")} {skippedText(done)}</span>}
                {done.qbo && done.renamed > 0 && <span className="block">{t("monthExport.renamed", { count: done.renamed })}</span>}
                {done.qbo && done.numbered > 0 && <span className="block">{t("monthExport.numbered", { count: done.numbered })}</span>}
                {!done.complete && <span className="block">{t("monthExport.partial")}</span>}
              </span>
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-100 p-4 sm:p-5">
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-100">
            {t("monthExport.close")}
          </button>
          <button
            onClick={run}
            disabled={busy}
            data-track={format === "excel" ? "month_export_excel" : "month_export_qbo"}
            className="inline-flex items-center gap-2 rounded-xl bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            <Download className="h-4 w-4" />
            {busy ? t("monthExport.loading", { count: loaded }) : t("monthExport.download")}
          </button>
        </div>
      </div>
    </div>
  );
}
